import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import {
	currencyCodes,
	expenseCategoryInputTypes,
	expenseDistanceSources,
	expenseOcrStatuses,
	expenseRequestStatuses,
	expenseTripTypes,
} from "~/api/expenses/constants";
import { getApiClient, getSessionCookieHeader } from "~/utils/api-client";

const ExpenseRequestListInputSchema = z
	.object({
		status: z.enum(expenseRequestStatuses).optional(),
		year: z.number().int().min(2000).max(2100).optional(),
		month: z.number().int().min(1).max(12).optional(),
	})
	.optional();

const ExpenseCategorySchema = z.object({
	category_id: z.number().int().positive(),
	name: z.string(),
	input_type: z.enum(expenseCategoryInputTypes),
	price_per_km: z.string().nullable(),
	price_per_day: z.string().nullable(),
	requires_receipt: z.boolean(),
	auto_approve: z.boolean(),
	is_archived: z.boolean(),
	sort_order: z.number().int(),
});

const ExpenseRequestSummarySchema = z.object({
	expense_request_id: z.number().int().positive(),
	person_id: z.number().int().positive(),
	person_first_name: z.string(),
	person_last_name: z.string(),
	category_id: z.number().int().positive(),
	category_name: z.string(),
	description: z.string(),
	status: z.enum(expenseRequestStatuses),
	currency: z.enum(currencyCodes),
	total_amount: z.string(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
	receipt_count: z.number().int().nonnegative(),
});

const ExpenseRequestDetailsSchema = z.object({
	request: z.object({
		expense_request_id: z.number().int().positive(),
		person_id: z.number().int().positive(),
		category_id: z.number().int().positive(),
		description: z.string(),
		extra_information: z.string().nullable(),
		status: z.enum(expenseRequestStatuses),
		missing_required_receipt: z.boolean(),
		currency: z.enum(currencyCodes),
		total_amount: z.string(),
	}),
	category: ExpenseCategorySchema,
	person: z.object({
		person_id: z.number().int().positive(),
		first_name: z.string(),
		last_name: z.string(),
		email: z.string(),
	}),
	lines: z.array(
		z.object({
			expense_line_id: z.number().int().positive(),
			incurred_on: z.coerce.date(),
			amount: z.string().nullable(),
			unit_price: z.string().nullable(),
			quantity: z.string().nullable(),
			quantity_unit: z.enum(["km", "day"]).nullable(),
			origin_address: z.string().nullable(),
			destination_address: z.string().nullable(),
			trip_type: z.enum(expenseTripTypes).nullable(),
			distance_km: z.string().nullable(),
			distance_source: z.enum(expenseDistanceSources).nullable(),
			line_total: z.string(),
		}),
	),
	receipts: z.array(
		z.object({
			receipt_id: z.number().int().positive(),
			file_name: z.string(),
			mime_type: z.string(),
			file_size: z.number().int().nonnegative(),
			ocr_status: z.enum(expenseOcrStatuses),
			ocr_payload: z.record(z.string(), z.any()).nullable(),
			ocr_confidence: z.string().nullable(),
		}),
	),
	events: z.array(
		z.object({
			event_id: z.number().int().positive(),
			event_type: z.string(),
			note: z.string().nullable(),
			created_at: z.coerce.date(),
		}),
	),
});

const ReceiptViewResponseSchema = z.object({
	view_url: z.string(),
});

class ExpenseApiError extends Error {
	status: number;
	constructor(status: number, message: string) {
		super(message);
		this.name = "ExpenseApiError";
		this.status = status;
	}
}

async function throwExpenseApiError(result: {
	status: number;
	json: () => Promise<unknown>;
}): Promise<never> {
	let detail: string | undefined;
	try {
		const body = (await result.json()) as {
			error?: string;
			message?: string;
		};
		detail = body.message ?? body.error;
	} catch {
		// body wasn't JSON — fall through to defaults
	}

	const status: number = result.status;

	const defaults: Record<number, string> = {
		400: "No active organization",
		401: "Unauthorized",
		403: "Expenses are disabled for this organization",
		404: "Person not found",
	};

	throw new ExpenseApiError(
		status,
		detail ?? defaults[status] ?? `Server error (${status})`,
	);
}

const fetchExpenseCategories = createServerFn({ method: "GET" }).handler(
	async () => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api.expenses.categories.$get();

		if (!result.ok) {
			await throwExpenseApiError(result);
		}

		const payload = await result.json();
		return ExpenseCategorySchema.array().parse(payload);
	},
);

const fetchExpenseRequests = createServerFn({ method: "GET" })
	.validator(ExpenseRequestListInputSchema)
	.handler(async ({ data }) => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api.expenses.$get({
			query: {
				status: data?.status,
				year: data?.year?.toString(),
				month: data?.month?.toString(),
			},
		});

		if (!result.ok) {
			await throwExpenseApiError(result);
		}

		const payload = await result.json();
		return ExpenseRequestSummarySchema.array().parse(payload);
	});

const fetchExpenseRequest = createServerFn({ method: "GET" })
	.validator(z.number().int().positive())
	.handler(async ({ data: expense_request_id }) => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api.expenses[
			":expense_request_id"
		].$get({
			param: { expense_request_id: expense_request_id.toString() },
		});

		if (result.status === 404) {
			return null;
		}

		if (!result.ok) {
			await throwExpenseApiError(result);
		}

		const payload = await result.json();
		return ExpenseRequestDetailsSchema.parse(payload);
	});

const fetchReceiptViewUrl = createServerFn({ method: "GET" })
	.validator(
		z.object({
			expense_request_id: z.number().int().positive(),
			receipt_id: z.number().int().positive(),
		}),
	)
	.handler(async ({ data }) => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api.expenses[
			":expense_request_id"
		].receipts[":receipt_id"].view.$get({
			param: {
				expense_request_id: data.expense_request_id.toString(),
				receipt_id: data.receipt_id.toString(),
			},
		});

		if (!result.ok) {
			await throwExpenseApiError(result);
		}

		const payload = await result.json();
		return ReceiptViewResponseSchema.parse(payload);
	});

export const EXPENSES_KEYS = {
	all: ["expenses"] as const,
	categories: () => [...EXPENSES_KEYS.all, "categories"] as const,
	requests: () => [...EXPENSES_KEYS.all, "requests"] as const,
	requestList: (query?: z.infer<typeof ExpenseRequestListInputSchema>) =>
		[...EXPENSES_KEYS.requests(), "list", query ?? {}] as const,
	requestDetails: () => [...EXPENSES_KEYS.requests(), "detail"] as const,
	requestDetail: (expense_request_id: number) =>
		[...EXPENSES_KEYS.requestDetails(), expense_request_id] as const,
};

export type ExpenseCategory = Awaited<
	ReturnType<typeof fetchExpenseCategories>
>[number];
export type ExpenseRequestSummary = Awaited<
	ReturnType<typeof fetchExpenseRequests>
>[number];
export type ExpenseRequestDetails = NonNullable<
	Awaited<ReturnType<typeof fetchExpenseRequest>>
>;
export type ExpenseReceipt = ExpenseRequestDetails["receipts"][number];
export type ExpenseRequestListInput = z.infer<
	typeof ExpenseRequestListInputSchema
>;

export const expenseCategoriesQueryOptions = queryOptions({
	queryKey: EXPENSES_KEYS.categories(),
	queryFn: () => fetchExpenseCategories(),
});

export const expenseRequestsQueryOptions = (input?: ExpenseRequestListInput) =>
	queryOptions({
		queryKey: EXPENSES_KEYS.requestList(input),
		queryFn: () => fetchExpenseRequests({ data: input }),
	});

export const expenseRequestQueryOptions = (expense_request_id: number) =>
	queryOptions({
		queryKey: EXPENSES_KEYS.requestDetail(expense_request_id),
		queryFn: () => fetchExpenseRequest({ data: expense_request_id }),
	});

export { fetchReceiptViewUrl };
